import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/common";
import { useDepot } from "../context/DepotContext";
import { api, formatApiError } from "../lib/api";

function DepotCard({ depot, onSaved, delay }) {
  const [name, setName] = useState(depot.name);
  const [location, setLocation] = useState(depot.location || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(depot.name); setLocation(depot.location || ""); }, [depot]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/depots/${depot.id}`, { name, location });
      toast.success("Depot updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="rise relative overflow-hidden rounded-lg border border-slate-800 bg-slate-900 p-6 space-y-4" style={{ animationDelay: `${delay}ms` }} data-testid={`depot-card-${depot.code}`}>
      <div className="absolute -right-4 -top-8 font-display text-[140px] font-extrabold leading-none text-slate-800/60 select-none">{depot.code}</div>
      <div className="relative label-mono !text-amber-500">Depot code {depot.code}</div>
      <div className="relative space-y-1.5">
        <Label className="label-mono">Display name</Label>
        <Input required value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-950" data-testid={`depot-name-input-${depot.code}`} />
      </div>
      <div className="relative space-y-1.5">
        <Label className="label-mono">Location</Label>
        <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Main street" className="bg-slate-950" data-testid={`depot-location-input-${depot.code}`} />
      </div>
      <Button type="submit" disabled={busy} className="relative w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid={`depot-save-button-${depot.code}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
      </Button>
    </form>
  );
}

export default function Depots() {
  const { depots, reload } = useDepot();
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Owner" title="Depots" subtitle="Rename your three depots to match their real names and locations." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {depots.map((d, i) => <DepotCard key={d.id} depot={d} onSaved={reload} delay={i * 70} />)}
      </div>
    </div>
  );
}
